import { useEffect, useRef, useState } from 'react';
import './FilingCarousel.css';

/**
 * 3D carousel showing three steps of the filing flow (Search -> Confirm ->
 * Filed). Sits inside the hero, replacing the old flat balance-sheet mock.
 *
 * Interaction model — deliberately kept out of the page's vertical scroll:
 *   - HORIZONTAL wheel / trackpad swipe advances the carousel.
 *   - Vertical wheel scroll is ignored so the visitor can still scroll the
 *     page normally when their cursor happens to be over the scene.
 *   - Left / Right arrow keys advance when the scene has focus.
 *   - Clicking a back or mid card rotates it into the front.
 *   - Horizontal touch swipe (mobile / tablet) also advances.
 *
 * Positions are driven by data-rank (0 = front, 1 = mid, 2 = back). All
 * three cards share a 720ms cubic-bezier transition on transform, so
 * moving between ranks is a real 3D arc rather than a flat slide.
 */

const STEPS = ['Search', 'Confirm', 'Filed'] as const;
const N = 3;

export function FilingCarousel() {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [front, setFront] = useState(0);
  const [hintFaded, setHintFaded] = useState(false);
  const [paused, setPaused] = useState(false);
  const lockRef = useRef(false);

  // Auto-play: rotate to the next step every 2s. Pauses whenever the
  // scene is hovered / focused / being touched, so it never interrupts
  // a user who's actively interacting. Horizontal-wheel + click + touch
  // + keyboard interactions still work as before — auto-play is the
  // primary driver because horizontal-wheel became unreliable across
  // modern browsers (they hijack it for back/forward navigation).
  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => {
      if (lockRef.current) return;
      lockRef.current = true;
      setFront((f) => (f + 1) % N);
      setHintFaded(true);
      window.setTimeout(() => {
        lockRef.current = false;
      }, 720);
    }, 2000);
    return () => window.clearInterval(id);
  }, [paused]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const advance = (dir: 1 | -1) => {
      if (lockRef.current) return;
      lockRef.current = true;
      setFront((f) => (f + dir + N) % N);
      setHintFaded(true);
      // Matches the CSS transition duration.
      window.setTimeout(() => {
        lockRef.current = false;
      }, 720);
    };

    const onWheel = (e: WheelEvent) => {
      // Only horizontal wheel / trackpad swipes advance the carousel.
      // Vertical scroll passes through untouched so the visitor can
      // keep scrolling the page while their pointer is over the scene.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (Math.abs(e.deltaX) < 6) return;
      e.preventDefault();
      advance(e.deltaX > 0 ? 1 : -1);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        advance(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        advance(-1);
      }
    };

    let startX: number | null = null;
    let startY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      // Pause auto-play while the user is actively touching the scene.
      setPaused(true);
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (startX == null || startY == null) {
        setPaused(false);
        return;
      }
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      // Same rule as wheel: only horizontal gestures drive the deck,
      // so a vertical scroll gesture on mobile still scrolls the page.
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
        advance(dx < 0 ? 1 : -1);
      }
      startX = startY = null;
      // Resume auto-play a moment after the touch ends so the user
      // sees their own gesture settle before the next auto-advance.
      window.setTimeout(() => setPaused(false), 1500);
    };

    scene.addEventListener('wheel', onWheel, { passive: false });
    scene.addEventListener('keydown', onKey);
    scene.addEventListener('touchstart', onTouchStart, { passive: true });
    scene.addEventListener('touchend', onTouchEnd);
    return () => {
      scene.removeEventListener('wheel', onWheel);
      scene.removeEventListener('keydown', onKey);
      scene.removeEventListener('touchstart', onTouchStart);
      scene.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const clickCard = (i: number) => {
    if (i === front || lockRef.current) return;
    const forward = (i - front + N) % N;
    const backward = (front - i + N) % N;
    const dir: 1 | -1 = forward <= backward ? 1 : -1;
    // Advance and, if two hops are needed, queue the second one after
    // the first transition finishes.
    lockRef.current = true;
    setFront((f) => (f + dir + N) % N);
    setHintFaded(true);
    window.setTimeout(() => {
      lockRef.current = false;
      if (Math.min(forward, backward) === 2) {
        lockRef.current = true;
        setFront((f) => (f + dir + N) % N);
        window.setTimeout(() => {
          lockRef.current = false;
        }, 720);
      }
    }, 720);
  };

  const rank = (i: number) => (i - front + N) % N;

  return (
    <div
      ref={sceneRef}
      className="filing-scene"
      tabIndex={0}
      role="region"
      aria-label="Filing flow preview - auto-advances; hover to pause"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className={`fs-hint ${hintFaded ? 'faded' : ''}`}>
        <span className="glyph">↔</span>
        Swipe to explore
      </div>

      <div className="filing-stage">
        <div className="fs-ground" aria-hidden />

        {/* Step 1: Search */}
        <div
          className="filing-screen"
          data-rank={rank(0)}
          onClick={() => clickCard(0)}
          aria-label="Step 1 - search your company"
        >
          <div className="fs-bar">
            <div className="fs-dots">
              <span className="fs-dot red" />
              <span className="fs-dot amber" />
              <span className="fs-dot green" />
            </div>
            <div className="fs-url">🔒 dormantaccountsfiling.com/search</div>
          </div>
          <div className="fs-body">
            <div className="fs-co-title" style={{ fontSize: 14 }}>
              Find your company
            </div>
            <div className="fs-co-sub">Search by name or CH number</div>
            <div className="fs-search-input">
              DORMANT ACCOUNTS FILING
              <span className="fs-cursor-bar" />
            </div>
            <div className="fs-search-list">
              <div className="fs-search-item focused">
                <div>
                  <div className="name">DORMANT ACCOUNTS FILING LIMITED</div>
                  <div className="no">16519999 - incorporated 2024</div>
                </div>
                <span className="pill">Active</span>
              </div>
              <div className="fs-search-item">
                <div>
                  <div className="name">DORMANT ACCOUNTS FILING SERVICES LTD</div>
                  <div className="no">15020304 - incorporated 2023</div>
                </div>
                <span className="pill">Active</span>
              </div>
              <div className="fs-search-item">
                <div>
                  <div className="name">DORMANT ACCOUNTS UK LTD</div>
                  <div className="no">10203040 - incorporated 2020</div>
                </div>
                <span className="pill">Active</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Confirm */}
        <div
          className="filing-screen"
          data-rank={rank(1)}
          onClick={() => clickCard(1)}
          aria-label="Step 2 - confirm your figures"
        >
          <div className="fs-bar">
            <div className="fs-dots">
              <span className="fs-dot red" />
              <span className="fs-dot amber" />
              <span className="fs-dot green" />
            </div>
            <div className="fs-url">🔒 dormantaccountsfiling.com/confirm/16519999</div>
          </div>
          <div className="fs-body">
            <div className="fs-steps">
              <span>
                <span className="n">1</span>Company
              </span>
              <span className="active">
                <span className="n">2</span>Confirm
              </span>
              <span>
                <span className="n">3</span>Auth
              </span>
              <span>
                <span className="n">4</span>Pay
              </span>
            </div>
            <div className="fs-co-title">DORMANT ACCOUNTS FILING LIMITED</div>
            <div className="fs-co-sub">
              Company 16519999 - Period 1 Jan 2025 to 31 Dec 2025
            </div>
            <div className="fs-bs-mini">
              <div className="h">DORMANT ACCOUNTS FILING LIMITED</div>
              <div className="h sm">Balance sheet - 31 Dec 2025</div>
              <table>
                <tbody>
                  <tr>
                    <td>Current assets</td>
                    <td className="num">£1</td>
                  </tr>
                  <tr>
                    <td>Creditors &lt; 1 yr</td>
                    <td className="num">£0</td>
                  </tr>
                  <tr>
                    <td>Called-up share capital</td>
                    <td className="num">£1</td>
                  </tr>
                  <tr className="total">
                    <td>Net assets</td>
                    <td className="num">£1</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul className="fs-checklist-mini">
              <li>
                <span className="tick">✓</span> Dormant throughout the period
              </li>
              <li>
                <span className="tick">✓</span> Exempt from audit (s.480)
              </li>
              <li>
                <span className="tick">✓</span> Directors approve accounts
              </li>
            </ul>
            <div className="fs-mid-cta">Continue to auth code →</div>
          </div>
        </div>

        {/* Step 3: Filed */}
        <div
          className="filing-screen"
          data-rank={rank(2)}
          onClick={() => clickCard(2)}
          aria-label="Step 3 - filed and acknowledged"
        >
          <div className="fs-bar">
            <div className="fs-dots">
              <span className="fs-dot red" />
              <span className="fs-dot amber" />
              <span className="fs-dot green" />
            </div>
            <div className="fs-url">🔒 dormantaccountsfiling.com/success/ord_75e</div>
          </div>
          <div className="fs-body">
            <div className="fs-success-hero">
              <div className="fs-success-orb">✓</div>
              <div className="fs-success-title">Filed with Companies House</div>
              <div className="fs-success-sub">
                Acknowledgement received - 4 min 12 s
              </div>
            </div>
            <div className="fs-ack">
              <div className="k">CH Acknowledgement</div>
              <div className="v">XCFVBGT-5RTGVBNH</div>
            </div>
            <div className="fs-receipt">
              <div className="cell">
                <div className="k">Paid</div>
                <div className="v">£20.00</div>
              </div>
              <div className="cell">
                <div className="k">Company</div>
                <div className="v" style={{ fontSize: 12, letterSpacing: 0 }}>
                  DAF 16519999
                </div>
              </div>
            </div>
            <div className="fs-log">
              <div className="fs-log-row">
                <span className="tick">✓</span> iXBRL accounts generated (FRS 102 1A)
              </div>
              <div className="fs-log-row">
                <span className="tick">✓</span> Submitted via XML Gateway
              </div>
              <div className="fs-log-row">
                <span className="tick">✓</span> Receipt emailed to director
              </div>
            </div>
          </div>
        </div>

        {/* Orbital objects */}
        <div className="fs-orbital fs-coin" aria-hidden>
          <div className="fs-coin-face">£20</div>
        </div>
        <div className="fs-orbital fs-envelope" aria-hidden>
          <div className="fs-envelope-inner">
            <div className="fs-env-head">
              <span className="b">✓</span> Filing accepted
            </div>
            <div className="fs-env-line w70" />
            <div className="fs-env-line w50" />
            <div className="fs-env-line w60" />
          </div>
        </div>
        <div className="fs-orbital fs-badge" aria-hidden>
          <div className="fs-badge-face">✓</div>
        </div>
      </div>

      <div className="fs-indicator" aria-live="polite">
        <span className="label">
          {front + 1} · {STEPS[front]}
        </span>
        <span className="dots-i">
          <span className={`d ${front === 0 ? 'on' : ''}`} />
          <span className={`d ${front === 1 ? 'on' : ''}`} />
          <span className={`d ${front === 2 ? 'on' : ''}`} />
        </span>
      </div>
    </div>
  );
}
